/* iter150AG Part 26 — Help drawer.
 *
 * Slides in from the right on every page, sources content from
 * /api/sequencing/help/articles (markdown files on disk). Search is
 * client-side via Fuse.js. Deep-linkable: ``?help=<slug>`` opens the
 * drawer on that article (used by inline "Learn more →" chips).
 *
 * Persists open/closed + last-viewed article in localStorage so the
 * drawer comes back on the right article next visit.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Question, MagnifyingGlass, X, BookOpen, ArrowLeft } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import Fuse from "fuse.js";

import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import useIsModalOpen from "@/lib/useIsModalOpen";


const LS_OPEN = "seq_help_drawer_open";
const LS_LAST = "seq_help_last_slug";


export default function HelpDrawer() {
  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) === "1");
  // iter150AZ.52 — true whenever any modal is currently mounted.
  const modalOpen = useIsModalOpen();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSlug, setActiveSlug] = useState(() => localStorage.getItem(LS_LAST) || null);
  const [search, setSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useT();

  // Persist open state.
  useEffect(() => {
    localStorage.setItem(LS_OPEN, open ? "1" : "0");
  }, [open]);
  useEffect(() => {
    if (activeSlug) localStorage.setItem(LS_LAST, activeSlug);
  }, [activeSlug]);

  // Honour ``?help=<slug>`` deep links — open the drawer + jump to slug.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get("help");
    if (target) {
      setActiveSlug(target);
      setOpen(true);
    }
  }, [location.search]);

  // Lazy-fetch articles once on first open. iter150AI.2 — merge the
  // sequencing help corpus with the new dealer-quote section (in the
  // user's current i18n language). Re-fetches when language changes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/sequencing/help/articles").then(r => r.data?.items || []).catch(() => []),
      api.get(`/help/sections/dealer-quote?lang=${encodeURIComponent(lang || "en")}`)
         .then(r => (r.data?.items || []).map(a => ({
           ...a, tags: [...(a.tags || []), "dealer-quote"],
         })))
         .catch(() => []),
    ]).then(([seq, dq]) => {
      if (cancelled) return;
      // Dedup by slug — sequencing wins for older shared slugs.
      const merged = [];
      const seen = new Set();
      for (const a of [...seq, ...dq]) {
        if (!a?.slug || seen.has(a.slug)) continue;
        seen.add(a.slug);
        merged.push(a);
      }
      setArticles(merged);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, lang]);

  const fuse = useMemo(() => articles.length === 0
    ? null
    : new Fuse(articles, {
        keys: [{ name: "title", weight: 2 }, "tags", "body"],
        threshold: 0.4,
        ignoreLocation: true,
      }), [articles]);

  const filtered = search.trim().length > 0 && fuse
    ? fuse.search(search.trim()).map(r => r.item)
    : articles;

  const active = activeSlug
    ? articles.find(a => a.slug === activeSlug)
    : null;

  function dismissDeepLink() {
    // Strip ``?help=…`` from the URL when the user closes the drawer
    // so a refresh doesn't re-open it.
    const params = new URLSearchParams(location.search);
    if (params.has("help")) {
      params.delete("help");
      navigate({ search: params.toString() }, { replace: true });
    }
  }

  function close() {
    setOpen(false);
    dismissDeepLink();
  }

  return (
    <>
      {/* iter150AZ.52 — Hide the floating "?" while any modal is open
          so we don't render two competing dialog circles. The drawer
          itself can still be opened via deep-link (?help=…) — only
          the FAB launcher is suppressed. */}
      {!modalOpen && (
        <button type="button"
                onClick={() => setOpen(true)}
                data-testid="help-drawer-open-btn"
                title="Open help"
                aria-label="Open help"
                className="fixed bottom-5 right-5 z-30 w-11 h-11 rounded-full bg-blue-700 text-white shadow-lg hover:bg-blue-800 inline-flex items-center justify-center transition-transform hover:scale-105">
          <Question size={20} weight="bold" />
        </button>
      )}

      {open && (
        <div data-testid="help-drawer"
             className="fixed inset-0 z-40 flex justify-end">
          {/* Backdrop */}
          <button type="button" onClick={close}
                  aria-label="Close help"
                  className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />

          {/* Drawer */}
          <aside className="relative w-full max-w-[480px] h-full bg-white border-l border-slate-200 shadow-xl flex flex-col"
                 onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-2">
              {active && (
                <button type="button" onClick={() => setActiveSlug(null)}
                        data-testid="help-back-btn"
                        title="Back to index"
                        className="w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100">
                  <ArrowLeft size={14} weight="bold" />
                </button>
              )}
              <BookOpen size={16} className="text-blue-700" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800">
                {active ? "" : "Help"}
              </h2>
              <button type="button" onClick={close}
                      data-testid="help-close-btn"
                      aria-label="Close help"
                      className="ml-auto w-7 h-7 inline-flex items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100">
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Content */}
            {active ? (
              <ArticleBody article={active} />
            ) : (
              <>
                {/* Search */}
                <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
                  <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={search}
                            onChange={e => setSearch(e.target.value)}
                            data-testid="help-search"
                            placeholder="Search help…"
                            className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-sm focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                {/* Article index */}
                <div className="flex-1 overflow-y-auto"
                     data-testid="help-index">
                  {loading && (
                    <div className="p-6 text-center text-slate-400 text-xs">Loading…</div>
                  )}
                  {!loading && filtered.length === 0 && (
                    <div className="p-6 text-center text-slate-400 text-xs italic">
                      No articles match your search.
                    </div>
                  )}
                  <ul className="divide-y divide-slate-100">
                    {filtered.map(a => (
                      <li key={a.slug}>
                        <button type="button"
                                onClick={() => setActiveSlug(a.slug)}
                                data-testid={`help-article-${a.slug}`}
                                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors">
                          <div className="text-sm font-bold text-slate-800">{a.title}</div>
                          {a.tags?.length > 0 && (
                            <div className="text-[10px] text-slate-400 mt-0.5 flex gap-1 flex-wrap">
                              {a.tags.slice(0, 4).map(t => (
                                <span key={t} className="font-mono">#{t}</span>
                              ))}
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-[10px] text-slate-400 text-center py-2 border-t border-slate-100">
                  {articles.length} article{articles.length === 1 ? "" : "s"} · powered by Fuse.js search
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}


function ArticleBody({ article }) {
  return (
    <article data-testid={`help-body-${article.slug}`}
             className="flex-1 overflow-y-auto px-5 py-4 help-md text-xs leading-relaxed text-slate-700">
      <style>{`
        .help-md h1 { font-size: 18px; font-weight: 700; color: rgb(15,23,42); margin: 0 0 10px; font-family: 'Chivo', system-ui, sans-serif; }
        .help-md h2 { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(51,65,85); margin: 16px 0 6px; }
        .help-md h3 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(71,85,105); margin: 12px 0 4px; }
        .help-md p { margin: 6px 0; }
        .help-md ul, .help-md ol { margin: 6px 0; padding-left: 20px; }
        .help-md li { margin: 2px 0; }
        .help-md code { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 10.5px; background: rgb(241,245,249); padding: 1px 4px; border-radius: 2px; color: rgb(15,23,42); }
        .help-md table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
        .help-md th, .help-md td { border: 1px solid rgb(226,232,240); padding: 4px 6px; text-align: left; }
        .help-md th { background: rgb(248,250,252); font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; color: rgb(71,85,105); }
        .help-md strong { color: rgb(15,23,42); }
        .help-md a { color: rgb(29,78,216); text-decoration: none; }
        .help-md a:hover { text-decoration: underline; }
        .help-md blockquote { border-left: 3px solid rgb(191,219,254); background: rgb(239,246,255); padding: 6px 10px; margin: 8px 0; color: rgb(30,64,175); }
      `}</style>
      <ReactMarkdown>{article.body}</ReactMarkdown>
    </article>
  );
}
